import {
  Dialog,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
} from "@mui/material";
import type { Entity, NewEntity, LogoKind } from "../../../api/types";
import { EntityForm } from "./EntityForm";

export interface EntityFormDialogProps<Id> {
  open: boolean;
  onClose: () => void;
  onSave: (
    entity: NewEntity<string>,
    logoFile?: Blob | null,
    resetLogo?: boolean,
  ) => void;
  initialData?: Entity<Id, string> | null;
  defaultName?: string;
  title: string;
  error?: string;
  entityType: LogoKind;
}

export const EntityFormDialog = <Id extends string>({
  open,
  onClose,
  onSave,
  initialData,
  defaultName,
  title,
  error,
  entityType,
}: EntityFormDialogProps<Id>) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {initialData ? `Edit ${title}` : `Add ${title}`}
      </DialogTitle>
      {error && (
        <Alert severity="error" sx={{ mx: 3, mt: 1 }}>
          {error}
        </Alert>
      )}
      {/* Key forces remount when initialData changes, resetting state */}
      <EntityForm
        key={initialData ? initialData.id : `new:${defaultName ?? ""}`}
        initialData={initialData}
        defaultName={defaultName}
        entityType={entityType}
        onSave={(data, logo, reset) => {
          onSave(data, logo, reset);
          // Don't close here, wait for parent to close on success
        }}
        onCancel={onClose}
      />
    </Dialog>
  );
};
